import PropTypes from 'prop-types';
import { useState, useCallback, useMemo } from 'react';
// @mui
import Paper from '@mui/material/Paper';
import ClickAwayListener from '@mui/material/ClickAwayListener';
import InputBase, { inputBaseClasses } from '@mui/material/InputBase';
import { postRequest } from 'src/services';
import { endpoints } from 'src/utils/axios';
import { useSnackbar } from 'notistack';  // Importing the hook for enqueueSnackbar

// ----------------------------------------------------------------------

export default function KanbanTaskAdd({
  status,
  onAddTask,
  onCloseAddTask,
  reload,
  currentItem,
  columnId,
}) {
  const [ name, setName ] = useState('');
  const [ loading, setLoading ] = useState(false);
  const { enqueueSnackbar } = useSnackbar(); // Using the hook to access enqueueSnackbar

  const defaultTask = useMemo(
    () => ({
      id: 0,
      status,
      name: name.trim(),
      priority: 'medium',
      attachments: [],
      labels: [ 'task', 'task2' ], // Default labels as requested
      comments: [],
      assignee: [],
      due: [ '2025-04-10', null ], // Assuming due date as per the data request
      reporter: {
        id: 0, // Assuming some default reporter ID
        name: 'Sample Reporter',
        avatarUrl: 'https://example.com/avatar.jpg', // Placeholder
      },
    }),
    [ name, status ]
  );

  const handleKeyUpAddTask = useCallback(
    (event) => {
      if (event.key === 'Enter') {
        if (name) {
          handleCreateTask(); // Post the task data after adding it
        }
      }
    },
    [ name ]
  );
  const handleChangeName = useCallback((event) => {
    setName(event.target.value);
  }, []);
  const handleClickAddTask = useCallback(() => {
    if (name) {
      handleCreateTask(); // Post the task data after adding it
    } else {
      onCloseAddTask();
    }
  }, [ name, onCloseAddTask ]);

  const handleCreateTask = useCallback(async () => {
    if (!name) {
      enqueueSnackbar({ variant: 'warning', message: 'Task name cannot be empty!' });
      return;
    }

    setLoading(true);

    const formData = new FormData();
    formData.append('title', name);
    formData.append('description', '4');
    formData.append('order', '1');
    formData.append('type', 'task');
    formData.append('status', 'pending');
    formData.append('priority', 'normal');
    formData.append('due_date', '2025-04-10');
    formData.append('start_date', '2025-04-10');
    formData.append('labels[]', 'task');

    try {
      const columnIdNumber = columnId.match(/\d+$/)?.[ 0 ];
      const response = await postRequest(
        `${endpoints.admin_dashboard.marketing_management.board.card_cretae}/${columnIdNumber}/cards`, // Use columnId
        formData,
        true
      );

      if (response.metadata.success) {
        console.log("🚀 ~ handleCreateTask ~ response:", response)
        enqueueSnackbar({ variant: 'success', message: 'Task created successfully' });
        onCloseAddTask();
        setName('');
        reload();
      } else {
        enqueueSnackbar({ variant: 'error', message: response?.metadata?.message });
      }
    } catch (err) {
      console.error('Error while creating task:', err);
      setLoading(false);
    }
  }, [ name, reload, columnId, onCloseAddTask, enqueueSnackbar ]);

  return (
    <ClickAwayListener onClickAway={ handleClickAddTask }>
      <Paper
        sx={ {
          borderRadius: 1.5,
          bgcolor: 'background.default',
          boxShadow: (theme) => theme.customShadows.z1,
        } }
      >
        <InputBase
          autoFocus
          fullWidth
          placeholder="Task name"
          value={ name }
          onChange={ handleChangeName }
          onKeyDown={ (event) => {
            if (event.key === 'Enter') {
              event.preventDefault(); // Prevents newline
              handleCreateTask();
            }
          } }
          sx={ {
            px: 2,
            height: 56,
            [ `.${inputBaseClasses.input}` ]: {
              typography: 'subtitle2',
            },
          } }
        />

      </Paper>
    </ClickAwayListener>
  );
}

KanbanTaskAdd.propTypes = {
  onAddTask: PropTypes.func,
  onCloseAddTask: PropTypes.func,
  status: PropTypes.string,
  reload: PropTypes.func, // Added for reloading the UI
};
